import { useEffect, useRef, useState } from 'react';
import { Box, Drawer, Stack, Typography } from '@mui/material';
import CasButton from '../../CasButton/CasButton';
import { CasSecondaryButton } from '../../CasButton/CasSecondaryButton';
import CasIconButton from '../../CasButton/CasIconButton';
import { CasStackRow } from '../../CasStack/CasStackRow';
import renderFilter from './renderFilter';
import { emptyFilterValue, isFilterShown } from './advancedFilters';

interface ICasFilterDrawer {
  open: boolean;
  onClose: () => void;
  filters: any[];
  appliedValues: Record<string, any>;
  onApply: (values: Record<string, any>) => void;
  // A filter name whose control takes focus once the drawer is open (a clicked chip).
  focusName?: string | null;
  container?: HTMLElement | null | (() => HTMLElement | null);
}

// The element a user would tab to in each control: the Select's combobox div, or the input
// of an autocomplete, text, number or date field. Excludes the Select's hidden native input.
const FOCUSABLE = '[role="combobox"], input:not([type="hidden"]):not([aria-hidden="true"]), textarea';

/*
  Right-side drawer holding a staged edit of the advanced filters. Nothing refetches
  until Apply, which commits every edit at once (one request).

  `draft` holds ONLY what has been touched since the drawer opened; anything absent
  reads straight through to the applied value. Copying the applied values into the
  draft on open instead would leave the controls blank for the frame before that
  effect runs, and would make Apply re-send - and so re-fire the onChange of - every
  filter, not just the changed ones.
*/
const CasFilterDrawer = ({
  open,
  onClose,
  filters,
  appliedValues,
  onApply,
  focusName,
  container,
}: ICasFilterDrawer) => {
  const [draft, setDraft] = useState<Record<string, any>>({});
  const paperRef = useRef<HTMLDivElement>(null);

  /*
    Runs once the slide-in has finished rather than on `open`: the drawer's content is
    portalled, so it may not exist yet when `open` flips, and focusing a control while
    the paper is still translated off screen can scroll the wrong container. Focusing
    also scrolls the drawer's list to the control.
  */
  const focusRequested = () => {
    if (!focusName) return;
    const row = Array.from(
      paperRef.current?.querySelectorAll<HTMLElement>('[data-filter-name]') ?? []
    ).find((el) => el.dataset.filterName === focusName);
    row?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
  };

  // Drop the previous session's edits each time the drawer opens.
  useEffect(() => {
    if (open) setDraft({});
  }, [open]);

  const valueOf = (name: string) => (name in draft ? draft[name] : appliedValues[name] ?? null);

  const setOne = (name: string, value: any) => setDraft((d) => ({ ...d, [name]: value }));

  // The filters offered right now: `visibleWhen` reads the staged values, so picking a value
  // here shows or hides a filter that depends on it before Apply.
  const staged = { ...appliedValues, ...draft };
  const shownFilters = filters.filter((f) => isFilterShown(f, staged));

  /*
    Reset clears every filter, EXCEPT one that declares `resetValue` - for a control that
    cannot meaningfully be blank. Two kinds: something that isn't really a filter (SCE
    Reports' View, a mode switch whose null would leave the page with no view to render),
    and a default the API requires (the Customers RMC filter, which is rejected as null
    for BusDev users).

    Deliberately NOT `initialValue`: consumers set that to the filter's CURRENT value so a
    remounted table keeps its filters, so falling back to it made Reset a no-op for every
    filter that had ever been applied.
  */
  const resetAll = () =>
    setDraft(
      Object.fromEntries(
        filters.map((f) => [
          f.name,
          'resetValue' in f ? f.resetValue : emptyFilterValue(valueOf(f.name)),
        ])
      )
    );
  // An edit to a filter that is hidden again by the time of Apply is dropped with its control.
  const apply = () => {
    const shown = new Set(shownFilters.map((f) => f.name));
    onApply(Object.fromEntries(Object.entries(draft).filter(([name]) => shown.has(name))));
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      /*
        Deliberately left at the default z-index (theme.zIndex.drawer, 1200). Raising it
        above theme.zIndex.modal to clear the table's full-screen layer (.table-fullscreen,
        1300 in Table.scss) also puts it above MUI's own menus and popovers, which sit at
        modal level - every select's dropdown list then opens BEHIND the drawer. So the
        full-screen case is handled by portalling into that layer (`container`) rather
        than by out-bidding it. See the drawerContainer comment in CasTable.
      */
      ModalProps={{ container }}
      SlideProps={{ onEntered: focusRequested }}
      PaperProps={{ ref: paperRef, className: 'CasFilterDrawer', sx: { width: 340 } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Box sx={{ p: 2, pb: 0 }}>
          <CasStackRow alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Filters</Typography>
            <CasIconButton icon="vaadin:close" width={16} height={16} tooltip="Close filters" onClick={onClose} />
          </CasStackRow>
        </Box>

        {/*
          `control-no-margin` drops the 12px bottom / 8px right margin Input.scss puts on
          every MuiFormControl - stacked one per row those add up to a very airy list - so
          the gap between rows is exactly this Stack's spacing.
          pt gives the first control's floating label room; without it the label sits on
          the scroll container's edge and its top is clipped.
        */}
        <Stack
          className="control-no-margin"
          spacing="8px"
          sx={{ flex: 1, overflowY: 'auto', px: 2, pt: '8px' }}
        >
          {shownFilters.map((f) => (
            <Box key={f.name} data-filter-name={f.name}>
              {renderFilter(f, valueOf(f.name), (v) => setOne(f.name, v), { fullWidth: true })}
            </Box>
          ))}
        </Stack>

        <Box sx={{ p: 2, borderTop: '0.5px solid', borderColor: 'divider' }}>
          <CasStackRow alignItems="center" justifyContent="space-between">
            <CasButton size="small" variant="text" color="inherit" onClick={resetAll}>
              Reset
            </CasButton>
            <CasStackRow alignItems="center" gapSize="small">
              <CasSecondaryButton size="small" onClick={onClose}>
                Cancel
              </CasSecondaryButton>
              <CasButton size="small" onClick={apply}>
                Apply
              </CasButton>
            </CasStackRow>
          </CasStackRow>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default CasFilterDrawer;
